import { useRef, useState } from 'react';
import { processImage } from '../../../lib/image.js';
import WallpaperAdjust from '../../../components/WallpaperAdjust.jsx';

/* ---------- 当前 tab ---------- */
export default function CurrentTab({
  characters,
  activeId,
  presets,
  activePresetId,
  lorebook,
  onActivate,
  onActivatePreset,
  onGotoCharacters,
  onGotoPresets,
  onGotoLore,
  providerKey,
  providerBase,
  providerModel,
  wallpaper,
  onWallpaperChange,
  onProviderChange,
  onResetDefaults,
  onClose,
}) {
  const active = characters.find((c) => c.id === activeId);
  const activePreset = presets.find((p) => p.id === activePresetId);
  const enabledLore = lorebook.filter((e) => e.enabled);

  // —— 聊天壁纸 ——
  const wpInputRef = useRef(null);
  const [wpError, setWpError] = useState(null);
  const [wpBusy, setWpBusy] = useState(false);
  const [wpAdjust, setWpAdjust] = useState(false); // 位置调整器（2026-07-12）
  async function handleWallpaperPick(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许连续选同一张
    if (!file) return;
    setWpError(null);
    setWpBusy(true);
    try {
      // 壁纸要铺满整块屏，压得比聊天图（1280）松一点
      const processed = await processImage(file, { maxEdge: 1920, quality: 0.82 });
      const err = onWallpaperChange({ img: processed.preview });
      if (err) setWpError(err);
    } catch (err) {
      setWpError(err?.message || String(err));
    } finally {
      setWpBusy(false);
    }
  }

  return (
    <>
      <label className="ch-settings-label">
        当前激活 · <span className="ch-en">active character</span>
      </label>
      {active ? (
        <div className="cc-current">
          <span className="cc-current-avatar">{active.avatar || '✦'}</span>
          <div className="cc-current-meta">
            <div className="cc-current-name">{active.name}</div>
            <div className="cc-current-bio">
              {(active.personality || '（无人格描述）').slice(0, 60)}
              {active.personality && active.personality.length > 60 ? '……' : ''}
            </div>
          </div>
        </div>
      ) : (
        <div className="cc-current cc-current-empty">还没有激活的角色卡</div>
      )}

      {characters.length > 1 && (
        <div className="cc-quick-switch">
          {characters.map((c) => (
            <button
              key={c.id}
              className={'cc-pill' + (c.id === activeId ? ' on' : '')}
              onClick={() => onActivate(c.id)}
            >
              <span className="cc-pill-av">{c.avatar || '✦'}</span>
              {c.name}
            </button>
          ))}
        </div>
      )}

      <button className="ch-tool-btn cc-goto" onClick={onGotoCharacters}>
        管理角色卡 →
      </button>

      <div className="ch-settings-divider" />

      {/* —— 当前激活预设 —— */}
      <label className="ch-settings-label">
        当前预设 · <span className="ch-en">active preset</span>
      </label>
      {activePreset ? (
        <div className="cc-preset-row">
          <div className="cc-preset-row-name">
            <span className="cc-preset-dot" />
            {activePreset.name}
          </div>
          <div className="cc-preset-row-meta">
            t={activePreset.temperature.toFixed(2)} · max={activePreset.max_tokens}
            {activePreset.system_addition?.trim() && (
              <span className="cc-preset-row-has">· 含风格规则</span>
            )}
          </div>
        </div>
      ) : (
        <div className="cc-current cc-current-empty">还没有激活的预设</div>
      )}
      {presets.length > 1 && (
        <div className="cc-quick-switch">
          {presets.map((p) => (
            <button
              key={p.id}
              className={'cc-pill' + (p.id === activePresetId ? ' on' : '')}
              onClick={() => onActivatePreset(p.id)}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}
      <button className="ch-tool-btn cc-goto" onClick={onGotoPresets}>
        管理预设 →
      </button>

      <div className="ch-settings-divider" />

      {/* —— 当前世界书概况 —— */}
      <label className="ch-settings-label">
        世界书 · <span className="ch-en">lorebook</span>
      </label>
      <div className="cc-preset-row">
        <div className="cc-preset-row-name">
          <span className="cc-preset-dot" />
          {enabledLore.length > 0
            ? `${enabledLore.length} 条已启用 / 共 ${lorebook.length} 条`
            : lorebook.length > 0
              ? `共 ${lorebook.length} 条 · 全部停用`
              : '还没有任何条目'}
        </div>
        {enabledLore.length > 0 && (
          <div className="cc-preset-row-meta">
            {enabledLore
              .slice(0, 3)
              .map((e) => e.keys?.[0] || '（无关键词）')
              .join(' · ')}
            {enabledLore.length > 3 && ` · 等 ${enabledLore.length} 条`}
          </div>
        )}
      </div>
      <button className="ch-tool-btn cc-goto" onClick={onGotoLore}>
        管理世界书 →
      </button>

      <div className="ch-settings-divider" />

      {/* —— 聊天壁纸（2026-07-04） —— */}
      <label className="ch-settings-label">
        聊天壁纸 · <span className="ch-en">wallpaper</span>
      </label>
      <p className="ch-settings-hint">
        选一张图铺在聊天背景上（只存这台设备——手机竖屏电脑横屏，各选各的刚好）。
        字看不清就把「遮罩」往右拉。
      </p>
      {wallpaper?.img ? (
        <div className="cc-wp-row">
          <img className="cc-wp-thumb" src={wallpaper.img} alt="当前壁纸" />
          <div className="cc-wp-ctrl">
            <label className="ch-settings-label cc-row-label cc-wp-label">
              遮 罩 · <span className="ch-en">dim</span>
              <span className="cc-slider-value">{wallpaper.dim}%</span>
            </label>
            <div className="cc-slider-wrap">
              <input
                type="range"
                min="0"
                max="90"
                step="5"
                value={wallpaper.dim}
                onChange={(e) =>
                  onWallpaperChange({ dim: parseInt(e.target.value, 10) })
                }
                className="cc-slider"
              />
              <div className="cc-slider-hint">
                <span>图清楚</span>
                <span>字清楚</span>
              </div>
            </div>
            <label className="cc-toggle cc-wp-blur" title="把图揉成一片朦胧的色彩">
              <input
                type="checkbox"
                checked={!!wallpaper.blur}
                onChange={(e) => onWallpaperChange({ blur: e.target.checked })}
              />
              <span className="cc-toggle-track">
                <span className="cc-toggle-thumb" />
              </span>
              <span className="cc-wp-blur-text">朦胧 · 模糊成氛围</span>
            </label>
            <div className="cc-wp-btns">
              <button
                className="ch-tool-btn"
                onClick={() => setWpAdjust(true)}
                title="拖动定位 + 缩放，把图摆到你想要的样子"
              >
                调整位置
              </button>
              <button
                className="ch-tool-btn"
                onClick={() => wpInputRef.current?.click()}
                disabled={wpBusy}
              >
                {wpBusy ? '处理中…' : '换一张'}
              </button>
              <button
                className="ch-tool-btn"
                onClick={() => {
                  setWpError(null);
                  onWallpaperChange({ img: null });
                }}
              >
                恢复星空
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          className="ch-tool-btn cc-goto"
          onClick={() => wpInputRef.current?.click()}
          disabled={wpBusy}
        >
          {wpBusy ? '处理中…' : '选一张图 →'}
        </button>
      )}
      {wpError && <p className="cc-wp-err">{wpError}</p>}
      <input
        ref={wpInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleWallpaperPick}
      />
      {/* 聊天壁纸 · 位置调整器（全屏浮层，2026-07-12 铃点的：图对不上屏可拖可缩） */}
      {wpAdjust && wallpaper?.img && (
        <WallpaperAdjust
          img={wallpaper.img}
          pos={wallpaper.pos}
          dim={wallpaper.dim}
          blur={wallpaper.blur}
          onSave={(pos) => onWallpaperChange({ pos })}
          onClose={() => setWpAdjust(false)}
        />
      )}

      <div className="ch-settings-divider" />

      <label className="ch-settings-label">
        Provider · <span className="ch-en">model &amp; key</span>
      </label>
      <p className="ch-settings-hint">
        三个都<b>留空</b>就用后端默认（现在是 gemai 中转）。
        <br />
        填了就用你自己的覆盖（key 只存在你这台设备）。
      </p>
      <input
        type="password"
        className="ch-settings-input ch-settings-input-sm"
        value={providerKey}
        onChange={(e) => onProviderChange({ key: e.target.value })}
        placeholder="API Key（留空 = 用后端默认）"
        autoComplete="off"
      />
      <input
        type="text"
        className="ch-settings-input ch-settings-input-sm"
        value={providerBase}
        onChange={(e) => onProviderChange({ base: e.target.value })}
        placeholder="Base URL（如 https://api.anthropic.com）"
        autoComplete="off"
        spellCheck="false"
      />
      <input
        type="text"
        className="ch-settings-input ch-settings-input-sm"
        value={providerModel}
        onChange={(e) => onProviderChange({ model: e.target.value })}
        placeholder="Model（如 claude-sonnet-5）"
        autoComplete="off"
        spellCheck="false"
        list="ch-model-suggestions"
      />
      {/* 建议列表≠限制：这个框可以自由输入任何模型 ID。
          顺序=最新最强在前（2026-07-28 向官方 /v1/models 核对过在售） */}
      <datalist id="ch-model-suggestions">
        <option value="claude-opus-5-5" />
        <option value="claude-opus-5" />
        <option value="claude-sonnet-5" />
        <option value="claude-opus-4-8" />
        <option value="claude-haiku-4-5" />
      </datalist>

      <div className="ch-settings-actions">
        <button className="ch-tool-btn" onClick={onResetDefaults}>
          全部恢复默认
        </button>
        <button className="ch-tool-btn on" onClick={onClose}>
          完 成
        </button>
      </div>
    </>
  );
}

